import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusChip } from '@monigo/ui';
import type { TransferRow as TR } from './api.js';

export function TransferRow({ transfer }: { transfer: TR }) {
  const { t, i18n } = useTranslation();
  const created = new Date(transfer.created_at);
  const title = transfer.beneficiary_name?.trim() || t('transfers.unknownRecipient');

  return (
    <Link
      to={`/transfers/${transfer.id}`}
      className="flex items-start gap-4 border-b border-border py-5"
    >
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-surface-elevated text-primary"
        aria-hidden="true"
      >
        <PayoutMethodIcon method={transfer.beneficiary_payout_method ?? null} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-2xl font-medium text-text">{title}</p>
        <p className="mt-1 font-mono text-xs text-text-muted">{transfer.reference_code}</p>
        <p className="mt-1">
          <StatusChip status={transfer.status} />
        </p>
      </div>
      <div className="text-right">
        <p className="text-2xl font-medium text-text">
          {transfer.amount_send} {transfer.from_currency}
        </p>
        <p className="text-lg text-text-muted">
          {created.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
        </p>
      </div>
    </Link>
  );
}

function PayoutMethodIcon({ method }: { method: string | null }) {
  if (method === 'bank') return <BankIcon />;
  if (method === 'mobile_money') return <MobileMoneyIcon />;
  return <CheckIcon />;
}

function MobileMoneyIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
      <path d="M9 6.5h6" />
      <path d="M12 10.5v4" />
      <path d="M10 12.5h4" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 10l9-6 9 6" />
      <path d="M4.5 10v8" />
      <path d="M9 10v8" />
      <path d="M15 10v8" />
      <path d="M19.5 10v8" />
      <path d="M2.5 20.5h19" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}
